/*
Publicacion en Mercado Libre
Autor: Benjamín León
Fecha: 2025-03-23
*/
import { useEffect, useState } from 'react'

import Vehicle from '../interfaces/Vehicle'
import useMeliFetcher from '../hooks/useMeliFetcher'
import { useAuth } from '../hooks/useAuth'
import { Rule, validateFields } from '../utils/validateForm'
import useMeliLogin from '../hooks/useMeliLogin'
import useMeliPublisher from '../hooks/useMeliPublisher'
import { BranchDto } from '../interfaces/Branch'
import useBranchesFetcher from '../hooks/useBranchesFetcher'
import useLocalization from '../hooks/useLocalization'
import Region from '../interfaces/Region'
import { invertedCharacteristics } from '../utils/characteristicTranslate'
import usePublicationDataSaver from '../hooks/usePublicationDataSaver'
import useMeliPublishingCore from '../hooks/useMeliPublishingCore'
import { autoSelectColor } from '../utils/colorMappingHelpers'

import InputTextArea from './InputTextArea'
import InputField from './InputField'
import ConfirmationModal from './ConfirmationModal'
import DisplayError from './DisplayError'
import { Info } from './MessageInfo'
import { ButtonAction } from './Buttons'
import { SelectField } from './SelectField'

interface MeliData {
  type: string
  body: string
  brand: string
  model: string
  year: number
  fuel: string
  gearbox: string
  comments: string
  color: string
  doors: number
  version: string
  title: string
  street: string
  price: number
}
// Por ahora el soloBoton
const Meli: React.FC<{ vehicle: Vehicle; onRefresh: () => void }> = ({ vehicle, onRefresh }) => {
  const { meliStart, errorMeliStart, meliTokenRefresh, errorMeliTokenRefresh } = useMeliLogin()
  const [isLogged, setIsLogged] = useState<boolean>(false)
  const { userData } = useAuth()
  const { buildMeliPayload } = useMeliPublishingCore()
  // Los valores iniciales. Notese que usamos "xxx_format" en los que
  // vienen desde el vehiculo y son "combos" fijos
  const [formData, setFormData] = useState<MeliData>({
    type: convertVehicleType(vehicle.type),
    body: vehicle.body_format,
    brand: vehicle.brand,
    model: vehicle.model,
    year: vehicle.year,
    fuel: vehicle.fuel_format,
    gearbox: vehicle.gearbox_format,
    comments: '', // Initialize with empty comments
    color: vehicle.color,
    version: vehicle.version,
    title: makeTitle(vehicle),
    doors: 0,
    street: '',
    price: vehicle.price || 0,
  })

  const [brands, setBrands] = useState<string[]>([])
  const [types, setTypes] = useState<Record<string, string>>({})
  const [bodies, setBodies] = useState<string[]>([])
  const [colors, setColors] = useState<string[]>([])
  const [fuel, setFuel] = useState<string[]>([])
  const [gearbox, setGearbox] = useState<string[]>([])
  const [regions, setRegions] = useState<Region[]>([])
  const { meliCategoryAttr, errorMeliCategoryAttr, meliTypes, errorMeliTypes, meliLogged, errorMeliLogged } =
    useMeliFetcher()

  const {
    meliPublish,
    errorMeliPublish,
    meliSetPublished,
    errorMeliSetPublished,
    meliUpdateData,
    errorMeliUpdateData,
    meliUpdateDescription,
    errorMeliUpdateDescription,
    loading,
  } = useMeliPublisher()
  const {
    savePublicationData,
    loadPublicationData,
    loading: savingLoading,
    error: saveError,
    clearError,
  } = usePublicationDataSaver()

  const [allBodies, setAllBodies] = useState<string[]>([])
  const [infoMessage, setInfoMessage] = useState<string>('')
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [shortDescriptionModalOpen, setShortDescriptionModalOpen] = useState<boolean>(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [faltaMsg, setFaltaMsg] = useState<string>('')
  const [saveMessage, setSaveMessage] = useState<string>('')
  const [isDraftLoading, setIsDraftLoading] = useState<boolean>(false)
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState<boolean>(false)
  const [externalApisComplete, setExternalApisComplete] = useState<boolean>(false)
  const [disabled, setDisabled] = useState<boolean>(false)
  const [branch, setBranch] = useState<BranchDto>()
  const [branchRegionId, setBranchRegionId] = useState<string>('')
  const { fetchBranchDetail } = useBranchesFetcher()
  const { fetchMunicipalities, errorMunicipalities, fetchRegionDetail, errorRegion } = useLocalization()
  // Se deriva del vehículo en cada render, no se guarda en estado: publicar
  // actualiza el vehículo pero no vuelve a montar este componente, así que un
  // flag en estado se quedaba en `false` y seguía ofreciendo "Publicar" sobre un
  // aviso ya publicado. El payload sí leía el identificador nuevo, de modo que
  // la API terminaba republicando y Mercado Libre respondía 400.
  //
  // Se mira el identificador además de la fecha porque es el identificador lo
  // que hace que la API republique (buildMeliPayload lo manda y publishVehicle
  // decide con él): si los dos campos se desalinean, el botón tiene que seguir
  // al que manda.
  const publicado = !!vehicle.external_meli?.identifier || !!vehicle.external_meli?.publishedAt

  // Edit modes: 'unified' replaces separate 'data' and 'description' modes
  const [editMode, setEditMode] = useState<'none' | 'data' | 'description' | 'unified'>('none')
  const [originalDescription, setOriginalDescription] = useState<string>('')

  // Enhanced loading states for unified edit
  const [loadingState, setLoadingState] = useState<'none' | 'data' | 'description'>('none')

  // Separate validation states
  const [dataErrors, setDataErrors] = useState<{ [key: string]: string }>({})
  const [descriptionErrors, setDescriptionErrors] = useState<{ [key: string]: string }>({})

  const MIN_DESCRIPTION_LENGTH = 55

  const validateDescription = (description: string): boolean => {
    return description.trim().length >= MIN_DESCRIPTION_LENGTH
  }

  // Separate validation functions for edit modes
  const validateDataFields = (): boolean => {
    // Use same validation as publishing flow (except description)
    const fieldsToValidate = [
      { name: 'type', value: formData.type, rule: Rule.REQUIRED },
      { name: 'body', value: formData.body, rule: Rule.REQUIRED },
      { name: 'brand', value: formData.brand, rule: Rule.REQUIRED },
      { name: 'model', value: formData.model, rule: Rule.REQUIRED },
      { name: 'fuel', value: formData.fuel, rule: Rule.REQUIRED },
      { name: 'gearbox', value: formData.gearbox, rule: Rule.REQUIRED },
      { name: 'color', value: formData.color, rule: Rule.REQUIRED },
      { name: 'title', value: formData.title, rule: Rule.REQUIRED },
      { name: 'street', value: formData.street, rule: Rule.REQUIRED },
      // ❌ NO comments validation - handled by Edit Description
    ]

    if (formData.type === 'MLC1744') {
      fieldsToValidate.push({ name: 'doors', value: formData.doors.toString(), rule: Rule.GREATER_ZERO })
    }

    const validationErrors = validateFields(fieldsToValidate)
    setDataErrors(validationErrors)

    return Object.keys(validationErrors).length === 0
  }

  const validateDescriptionField = (): boolean => {
    const errors: { [key: string]: string } = {}

    if (!validateDescription(formData.comments)) {
      errors.comments = `Descripción debe tener al menos ${MIN_DESCRIPTION_LENGTH} caracteres`
    }

    setDescriptionErrors(errors)

    return Object.keys(errors).length === 0
  }

  // Combined validation for unified edit mode
  const validateUnifiedFields = (): boolean => {
    // Validate data fields (same as current validateDataFields)
    const dataValid = validateDataFields()

    // Validate description field (same as current validateDescriptionField)
    const descValid = validateDescriptionField()

    return dataValid && descValid
  }

  // Extracted API call functions for unified edit
  const performDataUpdate = async (): Promise<{ success: boolean; message?: string }> => {
    try {
      const organization = userData?.organization

      if (!organization || !branch) {
        return { success: false, message: 'Organización o sucursal no encontrada' }
      }

      const pictures = vehicle.pictures?.public

      if (!pictures) {
        return { success: false, message: 'Vehículo no tiene imágenes públicas' }
      }

      // Fetch region detail to get meliRegionId (same as publishing flow)
      const regionId = branchRegionId
      const region = await fetchRegionDetail(regionId)

      if (!region) {
        return { success: false, message: 'No se encontró la región para Mercado Libre' }
      }

      const characteristics = vehicle.characteristics.map((c) => invertedCharacteristics[c])

      // NEW: Use shared core for consistent data update payload
      const updateFormData = {
        ...formData,
        // ❌ Exclude description - handled by separate Edit Description API
        // CRITICAL: Use promo.message as source, NOT vehicle.comments (internal field)
        comments: vehicle.promo?.message || ' ', // Keep original description, don't send formData.comments
      }

      const publishingConfig = {
        vehicle,
        formData: updateFormData,
        userData,
        branch: { ...branch, meliRegionId: region.meliRegionId },
        publicImages: pictures,
        characteristics,
        region,
      }

      // Use shared core payload builder for data updates too
      const vehicleMeli = buildMeliPayload(publishingConfig)

      if (!userData?.profile) {
        return { success: false, message: 'Usuario no encontrado' }
      }

      const result = await meliUpdateData(vehicleMeli, userData.profile?._id ?? '')

      if (result && typeof result === 'object' && 'ok' in result && result.ok) {
        return { success: true }
      } else {
        const errorMsg =
          result && typeof result === 'object' && 'message' in result ? String(result.message) : 'Error desconocido'

        return { success: false, message: errorMsg }
      }
    } catch (error) {
      return { success: false, message: 'Error de conexión en actualización de datos' }
    }
  }

  const performDescriptionUpdate = async (): Promise<{ success: boolean; message?: string }> => {
    try {
      const identifier = vehicle.external_meli?.identifier

      if (!identifier) {
        return { success: false, message: 'Vehículo no tiene identificador de MercadoLibre' }
      }

      if (!userData?.profile) {
        return { success: false, message: 'Usuario no encontrado' }
      }

      const result = await meliUpdateDescription(
        vehicle._id,
        identifier,
        formData.comments,
        userData.profile?._id ?? '',
        originalDescription
      )

      if (result && typeof result === 'object' && 'ok' in result && result.ok) {
        return { success: true }
      } else {
        const errorMsg =
          result && typeof result === 'object' && 'message' in result ? String(result.message) : 'Error desconocido'

        return { success: false, message: errorMsg }
      }
    } catch (error) {
      return { success: false, message: 'Error de conexión en actualización de descripción' }
    }
  }

  useEffect(() => {
    const meliLoggedAsync = async () => {
      const isMeliLogged = await meliLogged(userData?.profile?._id ?? '')
      setIsLogged(isMeliLogged == 1)

      // Y si esta loggeado, refrescamos el token
      // si es necesario
      if (isMeliLogged == 1) {
        await meliTokenRefresh(userData?.profile?._id ?? '')
      }
    }
    // Lo primero es ver si estamos logueados en mercado libre
    meliLoggedAsync()
    // Colocamos un numero grande para asegurarnos de hacer scroll hasta el final
    window.scrollTo({ top: 10000, behavior: 'smooth' })
  }, [])

  // Fetch category attributes regardless of login status
  useEffect(() => {
    const fetchCategoryAttrAsync = async () => {
      const all = await meliCategoryAttr()

      if (all) {
        const toutBrands = all.brands
        //Eliminamos las que tienen solo un caracter
        // o son solo numericos
        const pantaBrands = toutBrands.filter((b) => b.length > 1 && !Number(b))
        setBrands(pantaBrands)
        all.fuel.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        setFuel(all.fuel)
        all.gearbox.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        setGearbox(all.gearbox)
        all.bodies.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        setAllBodies(all.bodies)
        all.colors.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        setColors(all.colors)

        // Auto-select the correct color based on vehicle color for MercadoLibre
        if (all.colors && all.colors.length > 0) {
          const colorSelection = autoSelectColor(vehicle.color, all.colors, 'mercadolibre')

          if (colorSelection.selectedColor) {
            setFormData((prev) => ({ ...prev, color: colorSelection.selectedColor! }))
          }
        }
      }
    }

    const fetchTypesAsync = async () => {
      const allTypes = await meliTypes()
      // Los pasamos a un arreglo de strings
      //   const tout = Object.values(allTypes);
      //   tout.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      setTypes(allTypes)
    }

    // Fetch all external API data
    const loadAllData = async () => {
      await Promise.all([fetchCategoryAttrAsync(), fetchTypesAsync()])
    }

    const initializeComponentData = async () => {
      // Show loading message immediately and start minimum timer
      setIsDraftLoading(true)
      setTimeout(() => setMinimumTimeElapsed(true), 1500)

      // Step 1: Load draft data first to ensure it's available for merging.
      const draftResult = await loadPublicationData<MeliData>(vehicle._id, 'meli')
      const draftData = draftResult.success && draftResult.data ? draftResult.data : {}

      // Step 2: Establish the base data from the vehicle object.
      const baseData = {
        type: convertVehicleType(vehicle.type),
        body: vehicle.body_format,
        brand: vehicle.brand,
        model: vehicle.model,
        year: vehicle.year,
        fuel: vehicle.fuel_format,
        gearbox: vehicle.gearbox_format,
        // CRITICAL: Use promo.message as source, NOT vehicle.comments (internal field)
        comments: vehicle.promo?.message || ' ',
        color: vehicle.color,
        version: vehicle.version,
        title: makeTitle(vehicle),
        doors: 0,
        street: '',
        price: vehicle.price || 0,
      }

      // Step 3: Merge base data with draft data (draft takes precedence).
      const mergedData = { ...baseData, ...draftData }

      // Step 4: NOW, perform the 55-character validation on the final merged comments.
      const orgDefaultDescription = userData?.organization?.external?.meli?.defaultDescription
      let finalComments = mergedData.comments

      if (!finalComments || String(finalComments).trim().length < 55) {
        finalComments = orgDefaultDescription || ''
      }

      // Step 5: Create the final form data object for state.
      const initialFormData: MeliData = {
        ...mergedData,
        comments: finalComments,
      }

      setFormData(initialFormData)

      // Load external APIs after setting initial data
      await loadAllData()

      setExternalApisComplete(true)
      setIsDraftLoading(false)
    }

    initializeComponentData()
  }, [])

  // Only fetch branch data and check organization when logged in
  useEffect(() => {
    if (!isLogged) return

    const fetchBranchAsync = async () => {
      if (!vehicle.branch) {
        const msg = 'Asigna una sucursal al vehículo.'
        setFaltaMsg(msg)
        setDisabled(true)

        return
      }
      const branch = await fetchBranchDetail(vehicle.branch)

      if (!branch) {
        // raro
        setFaltaMsg('Sucursal no encontrada.')
        setDisabled(true)

        return
      }
      // Ahora, la calle mas el numero. Hay que buscar el primer
      // token de address que viene calle, comuna, ciudad

      const firstToken = (branch.address ?? '').split(',')[0].trim()

      let street = `${firstToken} ${branch.streetNumber}`

      if (branch.addressComponents) {
        street = `${street} - ${branch.addressComponents}`
      }
      setFormData((prev) => {
        return { ...prev, street: street }
      })
      let msg = null
      const comuna = branch?.municipality_format ?? ''

      if (!comuna) {
        msg = 'una comuna'
      } else if (!branch.address) {
        msg = 'una dirección'
      } else if (!branch.phone) {
        msg = 'un teléfono'
      } else if (!branch.whatsapp) {
        msg = 'un nro. WhatAapp'
      }

      if (msg) {
        const msgAux = `Por favor, asigna ${msg} a la sucursal.`
        setFaltaMsg(msgAux)
        setDisabled(true)

        return
      }
      setBranch(branch)

      const regs = await fetchMunicipalities()

      if (regs) {
        setRegions(regs)
        // Buscamos todas las regiones hasta encontrat aquella
        // que su comuna (texto) es igual a la del branch
        const region = regs.find((r) => r.name === comuna)

        if (region) {
          setBranchRegionId(region.regionId)
        }
      }
    }

    fetchBranchAsync()

    let sw = true

    if (!userData?.organization?.external?.meli?.enabled) {
      setFaltaMsg('Su organización no está autoriada para publicar en Mercado Libre.')
      sw = false
    }

    if (!sw) {
      setDisabled(true)
    }

    // Colocamos un numero grande para asegurarnos de hacer scroll hasta el final
    window.scrollTo({ top: 10000, behavior: 'smooth' })
  }, [isLogged])

  useEffect(() => {
    setBodies(allBodies)
  }, [formData.type, allBodies])

  // Publicar en Mercado Libre
  const handlePublishConfirm = async () => {
    setConfirmationModalOpen(false)

    if (!beforePublish()) {
      return
    }

    // NEW: Save draft data first before publishing
    console.log('Saving draft data before publishing...')
    const saveResult = await savePublicationData(vehicle._id, 'meli', formData as unknown as Record<string, unknown>)

    if (!saveResult.success) {
      setInfoMessage(`❌ Error al guardar datos: ${saveResult.message}`)

      return
    }

    console.log('✅ Draft data saved successfully, proceeding with publication')

    // Existing publish logic (unchanged)
    const characteristics = (vehicle.characteristics || []).map((c) => invertedCharacteristics[c])

    // Get region detail using branchRegionId
    const region = await fetchRegionDetail(branchRegionId)

    if (!region) {
      setInfoMessage('❌ Error: No se encontró la región para MercadoLibre')

      return
    }

    if (!userData || !branch) {
      setErrors({ general: 'Datos de usuario o sucursal no disponibles' })

      return
    }

    try {
      const vehicleToPublish = buildMeliPayload({
        vehicle,
        formData,
        userData,
        branch,
        publicImages: vehicle.pictures?.public || [],
        characteristics,
        region,
      })

      const result = await meliPublish(vehicleToPublish, userData?.profile?._id ?? '')

      if (result?.ok) {
        // Success case
        setInfoMessage('✅ Vehículo guardado y publicado exitosamente en MercadoLibre')

        if (userData?.profile) {
          await meliSetPublished(
            vehicle._id,
            userData?.profile?._id,
            result.inventoryIdentifier,
            result.permalink,
            result.title
          )
        }
        onRefresh()
      } else {
        // API returned ok: false
        setInfoMessage('❌ Error al publicar en MercadoLibre')
      }
    } catch (error) {
      // Network or other errors
      setInfoMessage(`❌ Error al publicar: ${error instanceof Error ? error.message : 'Error desconocido'}`)
    }
  }
  const handlePublishCancel = () => {
    setConfirmationModalOpen(false)
  }

  const onButtonPublish = () => {
    if (beforePublish()) {
      setConfirmationModalOpen(true)
    }
  }

  // Unified edit mode handlers
  const onUnifiedEditClick = () => {
    setEditMode('unified')
    setOriginalDescription(formData.comments)
  }

  // Main unified edit function
  const onConfirmUnifiedEdit = async () => {
    // Step 1: Validate both data AND description
    if (!validateUnifiedFields()) {
      return
    }

    try {
      // Step 2: Call Edit Data API first
      setLoadingState('data')
      const dataResult = await performDataUpdate()

      if (!dataResult.success) {
        setLoadingState('none')
        setFaltaMsg(`Error al actualizar datos: ${dataResult.message}`)

        return
      }

      // Step 3: Call Edit Description API second
      setLoadingState('description')
      const descResult = await performDescriptionUpdate()
      setLoadingState('none')

      if (!descResult.success) {
        setFaltaMsg(`Datos actualizados, pero error en descripción: ${descResult.message}`)

        return
      }

      // Step 4: Success - both operations completed
      setInfoMessage('Publicación actualizada exitosamente')
      setEditMode('none')
      setDataErrors({})
      setDescriptionErrors({})
      onRefresh()
    } catch (error) {
      setLoadingState('none')
      setFaltaMsg('Error inesperado durante la actualización')
    }
  }

  const onCancelEdit = () => {
    setEditMode('none')
    setDataErrors({})
    setDescriptionErrors({})
    setLoadingState('none')
    setFormData((prev) => ({ ...prev, comments: originalDescription }))
  }

  // Cuando se cambia la region
  const handleRegionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.target
    setBranchRegionId(value)
  }
  // Conectamos con Mercado libre
  const meliConnect = async () => {
    // Guardamos la ubicacion para poder volver después
    const currentPath = window.location.pathname + window.location.search
    sessionStorage.setItem('meliLoginRedirect', currentPath)

    // No longer storing profile ID in sessionStorage
    // It will be passed in the OAuth state parameter
    if (!userData?.profile) {
      console.warn('No profile ID available for MercadoLibre OAuth flow')
    }

    await meliStart()
  }

  // Sin ternario a propósito: este texto sólo se muestra en la rama `!publicado`.
  // Cuando el aviso ya está en línea el botón es "Editar Publicación / Republicar",
  // que va por updateData + updateDescription.

  // Button label helper for unified edit
  const getUnifiedButtonLabel = () => {
    if (loadingState === 'data') return 'Actualizando datos...'

    if (loadingState === 'description') return 'Actualizando descripción...'

    return 'Confirmar Cambios'
  }

  // Chequeos antes de publicar
  const beforePublish = (): boolean => {
    // Reemplazamos los datos por los que el usuario
    // escribe en la pantalla
    const fieldsToValidate = [
      { name: 'type', value: formData.type, rule: Rule.REQUIRED },
      { name: 'body', value: formData.body, rule: Rule.REQUIRED },
      { name: 'brand', value: formData.brand, rule: Rule.REQUIRED },
      { name: 'model', value: formData.model, rule: Rule.REQUIRED },
      { name: 'fuel', value: formData.fuel, rule: Rule.REQUIRED },
      { name: 'gearbox', value: formData.gearbox, rule: Rule.REQUIRED },
      { name: 'color', value: formData.color, rule: Rule.REQUIRED },
      { name: 'comments', value: formData.comments, rule: Rule.REQUIRED },
      { name: 'title', value: formData.title, rule: Rule.REQUIRED },
      { name: 'street', value: formData.street, rule: Rule.REQUIRED },
    ]

    if (formData.type === 'MLC1744') {
      fieldsToValidate.push({ name: 'doors', value: formData.doors.toString(), rule: Rule.GREATER_ZERO })
    }
    // chequear que esté all bien: combos con valor seleccionado
    const validationErrors = validateFields(fieldsToValidate)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)

      return false
    }

    // Add description length check - now that we have a character counter,
    // we still show the modal but it's more informative
    if (!validateDescription(formData.comments)) {
      setShortDescriptionModalOpen(true)

      return false
    }

    return true
  }

  const handleShortDescriptionConfirm = () => {
    setShortDescriptionModalOpen(false)
    setConfirmationModalOpen(true)
  }

  const handleShortDescriptionCancel = () => {
    setShortDescriptionModalOpen(false)
  }

  // Main form input change handler (no video logic)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Save draft functionality
  const handleSaveDraft = async () => {
    setSaveMessage('')
    clearError()

    const result = await savePublicationData(vehicle._id, 'meli', formData as unknown as Record<string, unknown>)

    if (result.success) {
      setSaveMessage('✅ Borrador guardado correctamente')
      setTimeout(() => setSaveMessage(''), 3000)
    } else {
      setSaveMessage(`❌ Error: ${result.message}`)
      setTimeout(() => setSaveMessage(''), 5000)
    }
  }

  // Cuando el usuario cambia el tipo (auto, moto, etc)
  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value
    setFormData((prev) => ({ ...prev, type: value, brand: '', model: '' }))
  }

  return (
    <div className="mb-4">
      <DisplayError msg={errorMeliLogged?.message} />
      <DisplayError msg={errorMeliTokenRefresh?.message} />
      <DisplayError msg={errorMunicipalities?.message} />
      <DisplayError msg={errorRegion?.message} />
      {!isLogged && (
        <div>
          <div className="flex justify-center mt-4">
            <ButtonAction label={`Conectar a Mercado Libre`} handleButtonClick={meliConnect} disabled={false} />
            <DisplayError msg={errorMeliStart?.message} />
          </div>
        </div>
      )}
      <div className="border">
        <DisplayError msg={faltaMsg} />
      </div>
      {isLogged && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <InputField
              label="Título"
              name="title"
              value={formData.title}
              type="text"
              handleInputChange={handleInputChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <SelectField
              label="Tipo"
              name="type"
              value={formData.type}
              options={types}
              handleInputChange={handleTypeChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <SelectFromArray
              label="Carrocería"
              name="body"
              value={formData.body}
              options={bodies}
              handleInputChange={handleInputChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <SelectFromArray
              label="Marca"
              name="brand"
              value={formData.brand}
              options={brands}
              handleInputChange={handleInputChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <InputField
              label="Modelo"
              name="model"
              value={formData.model}
              type="text"
              handleInputChange={handleInputChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <InputField
              label="Versión"
              name="version"
              value={formData.version}
              type="text"
              handleInputChange={handleInputChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <SelectFromArray
              label="Combustible"
              name="fuel"
              value={formData.fuel}
              options={fuel}
              handleInputChange={handleInputChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <SelectFromArray
              label="Transmisión"
              name="gearbox"
              value={formData.gearbox}
              options={gearbox}
              handleInputChange={handleInputChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <SelectFromArray
              label="Color"
              name="color"
              value={formData.color}
              options={colors}
              handleInputChange={handleInputChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <InputField
              label="N° de puertas"
              name="doors"
              value={formData.doors}
              type="number"
              handleInputChange={handleInputChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <SelectField
              label={'Comuna'}
              name={'municipality'}
              value={branchRegionId}
              options={regions}
              keyProp="regionId"
              valueProp="name"
              handleInputChange={handleRegionChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />

            <InputField
              label="Calle y N°"
              name="street"
              value={formData.street}
              type="string"
              handleInputChange={handleInputChange}
              errors={editMode === 'data' || editMode === 'unified' ? dataErrors : errors}
            />
          </div>
          <InputTextArea
            label="Descripción"
            name="comments"
            value={formData.comments}
            handleInputChange={handleInputChange}
            errors={editMode === 'description' || editMode === 'unified' ? descriptionErrors : errors}
            minLength={55}
            showCounter={true}
            placeholder={
              !formData.comments && userData?.organization?.external?.meli?.defaultDescription
                ? userData?.organization?.external?.meli?.defaultDescription
                : undefined
            }
          />

          {/* Display update errors */}
          <DisplayError msg={errorMeliUpdateData?.message} />
          <DisplayError msg={errorMeliUpdateDescription?.message} />

          <div>{errorMeliTypes?.message}</div>

          <div>{errorMeliCategoryAttr?.message}</div>

          <DisplayError msg={errorMeliPublish?.message} />
          <DisplayError msg={errorMeliSetPublished?.message} />
          <div className="flex justify-center mt-4">
            <Info message={infoMessage} trigger={Date.now()} onTimeout={() => setInfoMessage('')} noCenter={true} />
          </div>
          {(isDraftLoading || !minimumTimeElapsed || !externalApisComplete) &&
            !(saveMessage && saveMessage.startsWith('✅')) && (
              <div className="flex justify-center mt-2">
                <div className="text-sm font-bold text-blue-600">📝 Cargando datos...</div>
              </div>
            )}
          {(saveMessage || saveError) && (
            <div className="flex justify-center mt-2">
              <div
                className={`text-sm ${saveMessage && saveMessage.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}
              >
                {saveMessage || saveError}
              </div>
            </div>
          )}
          <div className="flex justify-center mt-4 gap-4">
            <ButtonAction
              label={savingLoading ? 'Guardando...' : 'Guardar'}
              handleButtonClick={handleSaveDraft}
              disabled={disabled || loading || savingLoading}
            />

            {!publicado ? (
              // Initial publication button
              <ButtonAction
                label={loading ? 'Publicando...' : 'Publicar en Mercado Libre'}
                handleButtonClick={() => onButtonPublish()}
                disabled={disabled || loading}
              />
            ) : editMode === 'none' ? (
              // Unified edit button for published vehicles
              <ButtonAction
                label="Editar Publicación / Republicar"
                handleButtonClick={onUnifiedEditClick}
                disabled={disabled || loading}
              />
            ) : editMode === 'unified' ? (
              // Unified edit confirmation buttons
              <>
                <ButtonAction
                  label={getUnifiedButtonLabel()}
                  handleButtonClick={onConfirmUnifiedEdit}
                  disabled={disabled || loading || loadingState !== 'none'}
                />
                <ButtonAction
                  label="Cancelar"
                  handleButtonClick={onCancelEdit}
                  disabled={loading || loadingState !== 'none'}
                />
              </>
            ) : null}
          </div>
          {shortDescriptionModalOpen && (
            <ConfirmationModal
              message={`La descripción debe tener al menos ${MIN_DESCRIPTION_LENGTH} caracteres para una buena publicación. Actualmente tiene ${formData.comments.trim().length} caracteres. ¿Desea publicar de todos modos?`}
              onConfirm={handleShortDescriptionConfirm}
              onCancel={handleShortDescriptionCancel}
            />
          )}
          {confirmationModalOpen && !(disabled || loading) && (
            <ConfirmationModal
              message="¿Estás seguro de publicar en Mercado Libre?"
              onConfirm={handlePublishConfirm}
              onCancel={handlePublishCancel}
            />
          )}
        </div>
      )}
    </div>
  )
}

const SelectFromArray: React.FC<{
  label: string
  name: string
  value?: string
  options: string[] | undefined
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  errors?: { [key: string]: string }
  disabled?: boolean
}> = ({ label, name, value, options, handleInputChange, errors }) => {
  // Convert array to record
  const optionsRecord: Record<string, string> = (options ?? []).reduce(
    (acc, option) => {
      acc[option.toString()] = option.toString()

      return acc
    },
    {} as Record<string, string>
  )

  return (
    <SelectField
      label={label}
      name={name}
      value={value?.toString()}
      options={optionsRecord}
      handleInputChange={handleInputChange}
      errors={errors}
    />
  )
}

const convertVehicleType = (type: string): string => {
  // Manual
  let meliCode

  switch (type) {
    case 'TYPES.VEHICLE.AUTOMOBILE':
      meliCode = 'MLC1744'
      break
    case 'TYPES.VEHICLE.MOTORCYCLE':
      meliCode = 'MLC1763'
      break
    case 'TYPES.VEHICLE.NAUTIC':
      meliCode = 'MLC1785'
      break
    default:
      meliCode = 'MLC1907'
      break
  }

  return meliCode
}
const makeTitle = (vehicle: Vehicle) => {
  return `${vehicle.brand} ${vehicle.model} ${vehicle.version}`
}
export default Meli
